import { isDefined } from '@doss/common-all'
import dayjs from 'dayjs'
import { compact, last, orderBy } from 'lodash'
import { LoaderIcon, LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbsInput,
  DossbotChatDocument,
  DossbotChatQuery,
  DossbotChatRunStatus,
  DossbotEventType,
  ToolRunStatus,
  useCancelDossbotChatRunMutation,
  useCreateDossbotChatRunMutation,
  useDossbotChatQuery,
} from '~/common/generated/apollo-operations'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Conversation, ConversationContent } from '~/components/ai-elements/conversation'
import { Message, MessageContent } from '~/components/ai-elements/message'
import {
  PromptInput,
  PromptInputBody,
  PromptInputSubmit,
  PromptInputTextarea,
} from '~/components/ai-elements/prompt-input'
import { Response } from '~/components/ai-elements/response'
import { DossbotChatEvent } from '~/components/dossbot/DossbotChatEvent'
import {
  useCreateThinkingEventTitle,
  useProcessEvent,
} from '~/components/dossbot/eventProcessingUtils'
import { DetailedToolRunStep } from '~/components/dossbot/types'
import { resizeTextarea, useTextareaResize } from '~/components/dossbot/useTextareaResize'

interface DossbotChatProps {
  chatId: string
  compactMessages?: boolean
  inputClassName?: string
}

export interface ResultMessage {
  role: 'user' | 'assistant'
  message: { type: 'text'; text: string }
}

export interface ProgressEvent {
  title: string
  result: string | null
  icon: LucideIcon
  type: DossbotEventType
}

/**
 * DossbotChat component that displays the chat history and the progress events for a given chat
 */
export function DossbotChat({ chatId, compactMessages, inputClassName }: DossbotChatProps) {
  const messageSize = compactMessages ? 'compact' : 'default'
  const { t } = useTypedTranslation()
  const processEvent = useProcessEvent()
  const createThinkingEventTitle = useCreateThinkingEventTitle()
  const [createDossbotChatRun] = useCreateDossbotChatRunMutation()
  const [cancelDossbotChatRun] = useCancelDossbotChatRunMutation()
  const { data: chatData, refetch } = useDossbotChatQuery({
    variables: { chatId },
    fetchPolicy: 'cache-and-network',
  })
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ResultMessage[]>([])
  const { objectId, objectType } = usePage()
  const [eventTitle, setEventTitle] = useState<{ title: string; icon: JSX.Element }>(
    createThinkingEventTitle,
  )
  const [progressEvents, setProgressEvents] = useState<ProgressEvent[]>([])
  const [pendingConfirmations, setPendingConfirmations] = useState<Array<DetailedToolRunStep>>([])

  // Track which chatId we've initialized progress events for
  const initializedChatIdRef = useRef<string | null>(null)
  const previousChatIdRef = useRef<string | null>(null)
  const textareaRef = useTextareaResize(input)

  // Clear pending confirmations immediately when switching chats
  useEffect(() => {
    if (!!previousChatIdRef.current && previousChatIdRef.current !== chatId) {
      setPendingConfirmations([])
    }
    previousChatIdRef.current = chatId
  }, [chatId])

  // Initialize the messages from the chat runs
  useEffect(() => {
    if (chatData?.dossbotChat.runs) {
      // Sort runs by createdAt in ascending order to ensure chronological message order
      const sortedRuns = orderBy(
        chatData.dossbotChat.runs,
        (run) => dayjs(run.createdAt).valueOf(),
        'asc',
      )
      // Show the user's prompt and AI response for each run
      const messages = sortedRuns.flatMap((run) => {
        const inputPrompt: ResultMessage = {
          role: 'user',
          message: { type: 'text', text: run.inputPrompt },
        }

        // Check if run completed successfully with a response
        if (run.response) {
          const response: ResultMessage = {
            role: 'assistant',
            message: { type: 'text', text: run.response },
          }
          return [inputPrompt, response]
        }

        // Check if run failed
        const failedEvent = run.events.find((e) => e.type === DossbotEventType.CHAT_RUN_FAILED)
        if (failedEvent) {
          const response: ResultMessage = {
            role: 'assistant',
            message: { type: 'text', text: t('dossbot.error_message') },
          }
          return [inputPrompt, response]
        }

        // Check if run was canceled
        const canceledEvent = run.events.find((e) => e.type === DossbotEventType.CHAT_RUN_CANCELED)
        if (canceledEvent) {
          const response: ResultMessage = {
            role: 'assistant',
            message: { type: 'text', text: t('dossbot.canceled_message') },
          }
          return [inputPrompt, response]
        }

        // Run is still in progress - only show the user prompt
        return [inputPrompt]
      })

      setMessages(messages)
    }
  }, [chatData?.dossbotChat.runs, t])

  // Initialize progress events from historical events for in-progress runs
  useEffect(() => {
    if (!chatData?.dossbotChat.runs) {
      return
    }

    // This prevents overwriting subscription updates when chatData changes due to cache updates
    const isNewChat = initializedChatIdRef.current !== chatId
    if (!isNewChat) {
      return
    }

    // Clear pending confirmations before processing historical events
    setPendingConfirmations([])
    // Find the last run that is in progress
    const allInProgressRuns = chatData.dossbotChat.runs.filter(
      (run) => run.status === DossbotChatRunStatus.IN_PROGRESS,
    )
    const inProgressRuns = compact([last(allInProgressRuns)])

    // If no runs are in progress, don't show any events
    if (inProgressRuns.length === 0) {
      setProgressEvents([])
      initializedChatIdRef.current = chatId
      return
    }

    // Collect events with their context from each in-progress run
    type EventWithContext = {
      event: DossbotChatQuery['dossbotChat']['runs'][number]['events'][number]
      chatRun: DossbotChatQuery['dossbotChat']['runs'][number]
      agentRun?: DossbotChatQuery['dossbotChat']['runs'][number]['agentRuns'][number]
      toolRunStep?: DossbotChatQuery['dossbotChat']['runs'][number]['agentRuns'][number]['toolRunSteps'][number]
    }
    // Collect events with their context from each in-progress run
    const allEvents: EventWithContext[] = inProgressRuns.flatMap((run) => [
      ...run.events.map((event) => ({ event, chatRun: run })),
      ...run.agentRuns.flatMap((agentRun) => [
        ...agentRun.events.map((event) => ({ event, chatRun: run, agentRun })),
        ...agentRun.toolRunSteps.flatMap((toolRunStep) =>
          toolRunStep.events.map((event) => ({ event, chatRun: run, agentRun, toolRunStep })),
        ),
      ]),
    ])

    // Sort events by creation time
    const sortedEvents = orderBy(allEvents, (item) => dayjs(item.event.createdAt).valueOf(), 'asc')

    // Process all events and collect results
    const results = sortedEvents.map(({ event, chatRun, agentRun, toolRunStep }) => ({
      result: processEvent({
        eventType: event.type as DossbotEventType,
        eventPayload: event.payload,
        chatRun,
        agentRun: { id: agentRun?.id ?? '', status: '', summary: agentRun?.summary ?? null },
        toolRunStep: {
          id: toolRunStep?.id ?? '',
          confirmationMessage: toolRunStep?.confirmationMessage ?? null,
        },
      }),
      event: { event, chatRun, agentRun, toolRunStep },
    }))

    // Collect progress events (filter out nullish values)
    const processedEvents = results.map(({ result }) => result.progressEvent).filter(isDefined)

    // Get the last event title that was returned
    const finalEventTitle = results
      .map(({ result }) => result.eventTitle)
      .filter(isDefined)
      .at(-1) // Get last element, or undefined if empty

    // Collect new pending confirmations
    const newPendingConfirmations = results
      .filter(({ result, event }) => result.shouldAddPendingConfirmation && event.toolRunStep)
      .map(({ event }) => {
        const toolRunStep = event.toolRunStep
        if (!toolRunStep) {
          return null
        }
        return {
          __typename: 'ToolRunStep' as const,
          id: toolRunStep.id,
          status: ToolRunStatus.PENDING,
          toolName: '',
          confirmationMessage: toolRunStep.confirmationMessage ?? null,
        } as DetailedToolRunStep
      })
      .filter(isDefined)

    // Update pending confirmations (single state update instead of multiple)
    if (newPendingConfirmations.length > 0) {
      setPendingConfirmations((prev) => [...prev, ...newPendingConfirmations])
    }

    setProgressEvents(processedEvents)
    setEventTitle(finalEventTitle ?? createThinkingEventTitle)

    initializedChatIdRef.current = chatId
  }, [chatData?.dossbotChat, chatId, refetch, t, processEvent, createThinkingEventTitle])

  // Create a new chat run
  const handleCreateNewChatRun = async (prompt: string) => {
    const chat = chatData?.dossbotChat
    if (!chatId || !prompt || !chat) {
      return
    }
    // Immediately show user message
    const optimisticUserMessage: ResultMessage = {
      role: 'user',
      message: { type: 'text', text: prompt },
    }
    setMessages((prev) => [...prev, optimisticUserMessage])

    let breadcrumb: BreadcrumbsInput | undefined
    if (objectId && objectType) {
      breadcrumb = { objectId, objectType }
    }

    try {
      setProgressEvents([
        {
          title: t('dossbot.planning_tasks'),
          result: null,
          icon: LoaderIcon,
          type: DossbotEventType.CHAT_RUN_STARTED,
        },
      ])
      setEventTitle(createThinkingEventTitle)

      await createDossbotChatRun({
        variables: { input: { chatId, inputPrompt: prompt, breadcrumb } },
        update: (cache, { data }) => {
          if (!data) {
            return
          }

          // Update the parent chat's cache with the information about the latest message run
          cache.writeQuery({
            query: DossbotChatDocument,
            variables: { chatId },
            data: {
              dossbotChat: {
                ...chat,
                runs: [...chat.runs, data.createDossbotChatRun],
              },
            },
          })
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createDossbotChatRun: {
            __typename: 'DossbotChatRun',
            completedAt: null,
            createdAt: dayjs().toISOString(),
            id: 'temp-id',
            inputPrompt: prompt,
            response: null,
            status: DossbotChatRunStatus.IN_PROGRESS,
          },
        },
      })
    } catch (error) {
      setProgressEvents([])
      console.error('Failed to create new chat run:', error)
    }

    setInput('')
  }

  // Cancel the current in-progress run
  const handleCancel = async () => {
    if (!chatData) {
      return
    }

    // Find the current in-progress run
    const inProgressRun = chatData.dossbotChat.runs.find(
      (run) => run.status === DossbotChatRunStatus.IN_PROGRESS,
    )

    if (!inProgressRun) {
      return
    }

    try {
      await cancelDossbotChatRun({
        variables: { runId: inProgressRun.id },
      })
      // Clear progress events and pending confirmations immediately to update UI
      setProgressEvents([])
      setPendingConfirmations([])
      // Refetch to get the updated run status
      await refetch()
    } catch (error) {
      console.error('Failed to cancel chat run:', error)
    }
  }

  if (!chatData) {
    return null
  }

  // Determine if the agent is processing or if there's a tool confirmation pending
  const hasInProgressRun = chatData.dossbotChat.runs.some(
    (run) => run.status === DossbotChatRunStatus.IN_PROGRESS,
  )

  const shownMessages = [...messages]
  let lastMessage = shownMessages.pop()
  if (lastMessage?.role === 'user') {
    shownMessages.push(lastMessage)
    lastMessage = undefined
  }

  return (
    <div className="flex h-full flex-col">
      <Conversation>
        <ConversationContent>
          {shownMessages.map(({ role, message }, index) => (
            <Message key={index} from={role}>
              <MessageContent size={messageSize}>
                <Response compact={compactMessages}>{message.text}</Response>
              </MessageContent>
            </Message>
          ))}
          <DossbotChatEvent
            chatId={chatId}
            eventTitle={eventTitle}
            initialPendingConfirmations={pendingConfirmations}
            onAddMessage={(message) => setMessages((prev) => [...prev, message])}
            progressEvents={progressEvents}
            setEventTitle={setEventTitle}
            setProgressEvents={setProgressEvents}
          />
          {lastMessage && (
            <Message from={lastMessage.role}>
              <MessageContent size={messageSize}>
                <Response compact={compactMessages}>{lastMessage.message.text}</Response>
              </MessageContent>
            </Message>
          )}
        </ConversationContent>
      </Conversation>

      <PromptInput
        className="relative items-end justify-end pt-4"
        onSubmit={(prompt) => {
          if (!prompt.text || hasInProgressRun) {
            return
          }

          handleCreateNewChatRun(prompt.text)
        }}
      >
        <PromptInputBody
          className={cn(
            'mx-4 mb-4 flex flex-row items-end justify-between rounded-lg border border-gray-4 bg-white px-4 py-3 dark:border-gray-6 dark:bg-gray-8',
            inputClassName,
          )}
        >
          <PromptInputTextarea
            ref={textareaRef}
            onChange={(e) => {
              setInput(e.target.value)
              resizeTextarea(e.target)
            }}
            placeholder={t('dossbot.enter_message')}
            value={input}
          />
          <PromptInputSubmit
            disabled={!chatId || (!input && !hasInProgressRun)}
            onClick={() => {
              if (hasInProgressRun) {
                handleCancel()
              }
            }}
            status={hasInProgressRun ? 'streaming' : 'ready'}
            variant={hasInProgressRun ? 'secondary' : 'primary'}
          />
        </PromptInputBody>
      </PromptInput>
    </div>
  )
}
